export const AUTH0_REFRESH_TOKEN_KEY = 'auth0_refresh_token';
export const AUTH0_TOKEN_EXPIRES_AT_KEY = 'auth0_token_expires_at';

const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export type Auth0Grant = {
  access_token: string;
  refresh_token?: string;
  expires_in?: number | string;
  scope?: string;
  token_type?: string;
};

const mergeUserInfoToken = (accessToken: string) => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    localStorage.setItem('userInfo', JSON.stringify({ ...userInfo, token: accessToken }));
  } catch {
    // ignore parse errors
  }
};

export const persistAuth0Grant = (grant: Auth0Grant) => {
  const access = grant.access_token?.trim();
  if (!access) {
    return;
  }

  localStorage.setItem('access_token', access);
  mergeUserInfoToken(access);

  const refresh = grant.refresh_token?.trim();
  if (refresh) {
    localStorage.setItem(AUTH0_REFRESH_TOKEN_KEY, refresh);
  }

  const expiresIn = Number(grant.expires_in);
  if (Number.isFinite(expiresIn) && expiresIn > 0) {
    localStorage.setItem(AUTH0_TOKEN_EXPIRES_AT_KEY, String(Date.now() + expiresIn * 1000));
  }
};

export const persistAuth0GrantFromHash = (hashParams: Record<string, unknown>) => {
  const access_token = hashParams?.access_token?.toString?.() || '';
  if (!access_token) {
    return;
  }

  persistAuth0Grant({
    access_token,
    refresh_token: hashParams?.refresh_token?.toString?.(),
    expires_in: hashParams?.expires_in?.toString?.(),
    scope: hashParams?.scope?.toString?.(),
    token_type: hashParams?.token_type?.toString?.(),
  });
};

export const buildAuth0ShiftRedirectUrl = (redirectUri: string, grant: Auth0Grant) => {
  const params = new URLSearchParams();
  params.set('access_token', grant.access_token);
  if (grant.scope) params.set('scope', grant.scope);
  if (grant.expires_in != null) params.set('expires_in', String(grant.expires_in));
  if (grant.token_type) params.set('token_type', grant.token_type);
  if (grant.refresh_token) params.set('refresh_token', grant.refresh_token);
  return `${redirectUri}#${params.toString()}`;
};

export const clearAuth0Session = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem(AUTH0_REFRESH_TOKEN_KEY);
  localStorage.removeItem(AUTH0_TOKEN_EXPIRES_AT_KEY);
};

export const getAuth0RefreshToken = (): string | null => {
  const refresh = localStorage.getItem(AUTH0_REFRESH_TOKEN_KEY);
  return refresh?.trim() || null;
};

export const getAuth0TokenExpiresAt = (): number | null => {
  const raw = localStorage.getItem(AUTH0_TOKEN_EXPIRES_AT_KEY);
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

export const shouldRefreshAuth0Token = (): boolean => {
  const refreshToken = getAuth0RefreshToken();
  if (!refreshToken) {
    return false;
  }

  const expiresAt = getAuth0TokenExpiresAt();
  if (!expiresAt) {
    return true;
  }

  return Date.now() >= expiresAt - REFRESH_BUFFER_MS;
};

export const getAuth0AccessToken = (): string | null => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    if (typeof userInfo?.token === 'string' && userInfo.token.trim()) {
      return userInfo.token.trim();
    }

    const direct = localStorage.getItem('access_token');
    if (direct?.trim()) {
      if (!userInfo?.token) {
        mergeUserInfoToken(direct.trim());
      }
      return direct.trim();
    }

    const hash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : '';
    if (hash) {
      const hashToken = new URLSearchParams(hash).get('access_token');
      if (hashToken?.trim()) {
        return hashToken.trim();
      }
    }
  } catch {
    // ignore parse errors
  }

  return null;
};

export const getAuth0AuthHeaders = (): Record<string, string> => {
  const token = getAuth0AccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};
